import { FiSliders, FiPlus, FiFileText, FiXCircle } from 'react-icons/fi';
import BottomSheet from '../../../../components/ui/BottomSheet';
import CloseButton from '../../../../components/ui/CloseButton';
import StatTile from '../../../../components/ui/StatTile';
import Button from '../../../../components/ui/Button';

/**
 * Session summary bottom sheet — opened via the header kebab.
 *
 * Props:
 *   open               — controlled open state
 *   onClose            — close handler
 *   siteName           — site display name shown as subtitle
 *   totalAssets        — total asset count (neutral stat)
 *   remaining          — untested count (warning when >0, success at 0)
 *   onSortAssets       — sort action handler
 *   onAddAsset         — add asset handler
 *   onExportTests      — export tests handler (opens Export sheet in PR4)
 *   onFinishSession    — finish handler (opens Finish sheet in PR5)
 *   onBackToDashboard  — navigate away (session stays active, resumable)
 *   onCancelSession    — cancel + discard session handler
 *   canCancelSession   — admin-only; hides the destructive cancel action from technicians
 */
export default function SessionSummarySheet({
  open,
  onClose,
  siteName,
  totalAssets = 0,
  remaining = 0,
  onSortAssets,
  onAddAsset,
  onExportTests,
  onFinishSession,
  onBackToDashboard,
  onCancelSession,
  canCancelSession = false,
}) {
  return (
    <BottomSheet open={open} onClose={onClose}>
      <div className="session-summary-sheet">

        {/* Header */}
        <div className="session-summary-sheet__header">
          <div className="session-summary-sheet__title-block">
            <h2 className="session-summary-sheet__title">Session summary</h2>
            <p className="session-summary-sheet__subtitle">{siteName}</p>
          </div>
          <CloseButton onClick={onClose} ariaLabel="Close session summary" />
        </div>

        {/* Stats */}
        <div className="session-summary-sheet__stats">
          <StatTile
            value={totalAssets}
            label="TOTAL ASSETS"
            layout="center"
            className="session-summary-sheet__stat"
          />
          <div className="session-summary-sheet__stat-divider" aria-hidden="true" />
          <StatTile
            value={remaining}
            label="REMAINING"
            layout="center"
            tone={remaining > 0 ? 'warning' : 'success'}
            className="session-summary-sheet__stat"
          />
        </div>

        {/* Action list */}
        <div className="session-summary-sheet__action-group" role="list">
          <button
            type="button"
            role="listitem"
            className="session-summary-sheet__action-row"
            onClick={onSortAssets}
          >
            <FiSliders size={18} aria-hidden className="session-summary-sheet__action-icon" />
            <span>Sort assets</span>
          </button>
          <button
            type="button"
            role="listitem"
            className="session-summary-sheet__action-row"
            onClick={onAddAsset}
          >
            <FiPlus size={18} aria-hidden className="session-summary-sheet__action-icon" />
            <span>Add asset</span>
          </button>
          <button
            type="button"
            role="listitem"
            className="session-summary-sheet__action-row session-summary-sheet__action-row--last"
            onClick={onExportTests}
          >
            <FiFileText size={18} aria-hidden className="session-summary-sheet__action-icon" />
            <span>Export Tests</span>
          </button>
        </div>

        {/* Primary actions */}
        <div className="session-summary-sheet__primary-actions">
          <Button
            variant="primary"
            className="session-summary-sheet__cta"
            onClick={onFinishSession}
          >
            Finish Session
          </Button>
          <Button
            variant="secondary"
            className="session-summary-sheet__cta"
            onClick={onBackToDashboard}
          >
            Back to Dashboard
          </Button>
        </div>

        {/* Cancel — separated by full-bleed divider, danger text only. Admin-only. */}
        {canCancelSession && (
          <div className="session-summary-sheet__cancel-section">
            <button
              type="button"
              className="session-summary-sheet__cancel-btn"
              onClick={onCancelSession}
            >
              <FiXCircle size={16} aria-hidden />
              <span>Cancel session</span>
            </button>
            <p className="session-summary-sheet__cancel-hint">
              Permanently deletes this session and its recorded tests. This cannot be undone.
            </p>
          </div>
        )}

      </div>
    </BottomSheet>
  );
}
